import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Tv, Search, Heart, Settings, LogOut, Menu, X, Zap, ListMusic, Crown, Users, HelpCircle } from 'lucide-react';
import { useState } from 'react';

export default function Navbar() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/browse?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setMobileOpen(false);
    }
  };

  const isActive = (path) => location.pathname === path;

  const navLinkClass = (path) =>
    `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold transition-colors ${
      isActive(path) ? 'bg-[#FF3860] text-white' : 'hover:bg-[#FFDE00] hover:text-[#0A0A0A]'
    }`;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FFDE00] border-b-2 border-[#0A0A0A]" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="navbar-logo">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/ebea1ea5-9731-4354-90db-f3840d8f2692/images/80514a52e5403549c758b4eb7a39bf04c9d4fbd1f3d392449159024de0890e38.png"
              alt="Retro Playground TV"
              className="w-9 h-9"
            />
            <span className="font-['Fredoka'] font-bold text-xl text-[#0A0A0A] hidden sm:block">Retro Playground TV</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-2">
            <Link to="/browse" className={navLinkClass('/browse')} data-testid="nav-browse">
              <Tv size={16} strokeWidth={2.5} /> Browse
            </Link>
            <Link to="/tv-schedule" className={navLinkClass('/tv-schedule')} data-testid="nav-tv-schedule">
              <Zap size={16} strokeWidth={2.5} /> TV Mode
            </Link>
            <Link to="/playlists" className={navLinkClass('/playlists')} data-testid="nav-playlists">
              <ListMusic size={16} strokeWidth={2.5} /> Playlists
            </Link>
            {user && (
              <Link to="/favorites" className={navLinkClass('/favorites')} data-testid="nav-favorites">
                <Heart size={16} strokeWidth={2.5} /> Favorites
              </Link>
            )}
            {user && (
              <Link to="/profiles" className={navLinkClass('/profiles')} data-testid="nav-profiles">
                <Users size={16} strokeWidth={2.5} /> Kids
              </Link>
            )}
            {isAdmin && (
              <Link to="/admin" className={navLinkClass('/admin')} data-testid="nav-admin">
                <Settings size={16} strokeWidth={2.5} /> Admin
              </Link>
            )}
          </div>

          {/* Search + Auth */}
          <div className="hidden md:flex items-center gap-3">
            <form onSubmit={handleSearch} className="relative" data-testid="search-form">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search shows..."
                className="w-44 pl-8 pr-3 py-1.5 text-sm border-2 border-[#0A0A0A] rounded-lg bg-white font-['Nunito'] focus:outline-none focus:ring-2 focus:ring-[#FF3860]"
                data-testid="search-input"
              />
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#4B5563]" strokeWidth={2.5} />
            </form>
            {user ? (
              <div className="flex items-center gap-2">
                <Link to="/settings" className="p-1.5 rounded-lg hover:bg-[#0A0A0A]/10 transition-colors" data-testid="settings-nav-btn" title="Settings">
                  <Settings size={16} strokeWidth={2.5} />
                </Link>
                <Link to="/faq" className="p-1.5 rounded-lg hover:bg-[#0A0A0A]/10 transition-colors" data-testid="faq-nav-btn" title="Help & FAQ">
                  <HelpCircle size={16} strokeWidth={2.5} />
                </Link>
                <span className="text-sm font-bold text-[#0A0A0A] font-['Nunito']">{user.name}</span>
                <button
                  onClick={logout}
                  className="neo-btn px-3 py-1.5 rounded-lg bg-white text-sm flex items-center gap-1"
                  data-testid="logout-btn"
                >
                  <LogOut size={14} strokeWidth={2.5} /> Logout
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/faq" className="p-1.5 rounded-lg hover:bg-[#0A0A0A]/10 transition-colors" data-testid="faq-nav-btn-guest" title="Help & FAQ">
                  <HelpCircle size={16} strokeWidth={2.5} />
                </Link>
                <Link
                  to="/login"
                  className="neo-btn px-4 py-1.5 rounded-lg bg-[#FF3860] text-white text-sm"
                  data-testid="login-nav-btn"
                >
                  Sign In
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2"
            data-testid="mobile-menu-btn"
          >
            {mobileOpen ? <X size={24} strokeWidth={2.5} /> : <Menu size={24} strokeWidth={2.5} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-[#FFDE00] border-t-2 border-[#0A0A0A] px-4 pb-4" data-testid="mobile-menu">
          <form onSubmit={handleSearch} className="relative mt-3 mb-3">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search shows..."
              className="w-full pl-8 pr-3 py-2 text-sm border-2 border-[#0A0A0A] rounded-lg bg-white"
              data-testid="mobile-search-input"
            />
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#4B5563]" strokeWidth={2.5} />
          </form>
          <div className="flex flex-col gap-1">
            <Link to="/browse" className={navLinkClass('/browse')} onClick={() => setMobileOpen(false)}>
              <Tv size={16} strokeWidth={2.5} /> Browse Shows
            </Link>
            <Link to="/tv-schedule" className={navLinkClass('/tv-schedule')} onClick={() => setMobileOpen(false)}>
              <Zap size={16} strokeWidth={2.5} /> TV Mode
            </Link>
            <Link to="/playlists" className={navLinkClass('/playlists')} onClick={() => setMobileOpen(false)}>
              <ListMusic size={16} strokeWidth={2.5} /> Playlists
            </Link>
            {user && (
              <Link to="/favorites" className={navLinkClass('/favorites')} onClick={() => setMobileOpen(false)}>
                <Heart size={16} strokeWidth={2.5} /> Favorites
              </Link>
            )}
            {isAdmin && (
              <Link to="/admin" className={navLinkClass('/admin')} onClick={() => setMobileOpen(false)}>
                <Settings size={16} strokeWidth={2.5} /> Admin
              </Link>
            )}
            {user ? (
              <button onClick={() => { logout(); setMobileOpen(false); }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold hover:bg-red-100">
                <LogOut size={16} strokeWidth={2.5} /> Logout ({user.name})
              </button>
            ) : (
              <Link to="/login" className="neo-btn px-4 py-2 rounded-lg bg-[#FF3860] text-white text-sm text-center" onClick={() => setMobileOpen(false)}>
                Sign In
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
